import type { DocumentData } from 'firebase/firestore'
import { doc, onSnapshot } from 'firebase/firestore'
import { useEffect, useState } from 'react'

import { db } from '@/app/config/firebase'

interface UseFirestoreDocumentResult<T = DocumentData> {
  data: T | null
  loading: boolean
  error: Error | null
}

export function useFirestoreDocument<T = DocumentData>(
  path: string
): UseFirestoreDocumentResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!path) {
      setData(null)
      setLoading(false)
      setError(null)
      return undefined
    }

    setLoading(true)
    setError(null)

    // Parse the path to get collection and document ID
    const pathParts = path.split('/').filter(Boolean)
    if (pathParts.length < 2 || pathParts.length % 2 !== 0) {
      const pathError = new Error(
        'Invalid document path. Path must be in format: collection/docId or collection/docId/subcollection/docId'
      )
      setError(pathError)
      setLoading(false)
      console.error('Firestore path error:', pathError.message)
      return undefined
    }

    const documentRef = doc(db, path)

    const unsubscribe = onSnapshot(
      documentRef,
      snapshot => {
        if (snapshot.exists()) {
          const documentData = {
            id: snapshot.id,
            ...snapshot.data(),
          } as T

          setData(documentData)
          setError(null)
        } else {
          setData(null)
        }
        setLoading(false)
      },
      err => {
        console.error(`❌ Firestore Error [${path}]:`, err)
        setError(err as Error)
        setLoading(false)
      }
    )

    return () => {
      unsubscribe()
    }
  }, [path])

  return { data, loading, error }
}
